import { useRef, useEffect } from 'react';
import { Loader } from 'common';
import { Icon } from 'common/icon';
import { Button } from 'common/new-button';
import styles from './styles.module.css';
import clsx from 'clsx';

type Props = {
  onClick: () => void;
  isFetching: boolean;
  withIcon?: boolean;
  isVisible?: boolean;
  isAuto?: boolean;
  variant?: 'base' | 'showMore' | 'transparent';
  description?: string;
  className?: string;
};

export const LoadMoreButton = ({
  onClick,
  isFetching,
  isVisible = false,
  isAuto = false,
  variant = 'base',
  description,
  withIcon = true,
  className,
}: Props) => {
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const onClickRef = useRef(onClick);
  const isFetchingRef = useRef(isFetching);
  const wasIntersectingRef = useRef(false);

  onClickRef.current = onClick;
  isFetchingRef.current = isFetching;

  useEffect(() => {
    if (!isAuto) {
      return;
    }

    const node = loadMoreRef.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const isIntersecting = entries[0]?.isIntersecting ?? false;
        const wasIntersecting = wasIntersectingRef.current;
        wasIntersectingRef.current = isIntersecting;

        if (isIntersecting && !wasIntersecting && !isFetchingRef.current) {
          onClickRef.current();
        }
      },
      { threshold: 0.1, rootMargin: '120px' }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      wasIntersectingRef.current = false;
    };
  }, [isAuto]);

  if (variant === 'showMore') {
    return (
      <button onClick={onClick} className={clsx(styles.all_link, styles.link)}>
        <div className={styles.item_thing_small}>
          {description && <p>{description}</p>}
        </div>
        {withIcon && (
          <div className={styles.item_arrow}>
            <Icon.Arrow width={20} height={20} />
          </div>
        )}
      </button>
    );
  }

  if (variant === 'transparent') {
    return (
      <div className={styles.loadMoreContainer} ref={loadMoreRef}>
        <button
          onClick={onClick}
          className={clsx(styles.all_link, styles.link_gap)}
        >
          <Icon.Circle />
          <div
            className={clsx(styles.item_thing_small, styles.item_thing_color)}
          >
            {description && <p>{description}</p>}
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className={styles.loadMoreContainer} ref={loadMoreRef}>
      {!isVisible && isFetching && (
        <Loader template="fitContent" size="big" type="primary" />
      )}
      {isVisible && (
        <Button
          text={isFetching ? 'Loading...' : 'Load more'}
          customStyle={clsx(styles.loadMoreButton, className)}
          onClick={onClick}
        />
      )}
    </div>
  );
};
